"use client";

import Link from "next/link";
import { createContext, useContext, useEffect, useRef, useState, type ReactNode } from "react";
import { usePathname, useRouter } from "next/navigation";
import { emailInitial, planLabel, useAuth, type AuthState } from "../lib/auth";
import { useFolders } from "../lib/folders-store";
import { AppToMarketingLink } from "./components/app-to-marketing-link";
import { ThemeToggle } from "../components/theme-toggle";

// --------------- Auth context ---------------
// Share auth state between shell, sidebar, and dashboard so we only
// call useAuth() once (one API hit, one loading cycle).

type AuthCtx = AuthState & { refresh: () => Promise<void>; signOut: () => void };
const AuthContext = createContext<AuthCtx | null>(null);

export function useAppAuth(): AuthCtx {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAppAuth must be used inside AppShell");
  return ctx;
}

// --------------- Shell ---------------

export function AppShell({ children }: { children: React.ReactNode }) {
  const pathname = usePathname();
  if (pathname?.startsWith("/ops-console")) {
    return <>{children}</>;
  }

  return <AuthenticatedAppShell>{children}</AuthenticatedAppShell>;
}

function AuthenticatedAppShell({ children }: { children: React.ReactNode }) {
  const router = useRouter();
  const pathname = usePathname();
  const auth = useAuth();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  useEffect(() => {
    if (auth.status === "anonymous") {
      // Preserve the page the guest was actually trying to reach so they
      // land there after signing in — not always /dashboard. Without this,
      // a marketing CTA like "Start a free audit" (→ /audit/new) bounced the
      // guest to the dashboard post-login, dropping their intent at the
      // moment of highest motivation. login-client reads ?next and decodes it.
      const intended =
        pathname && pathname !== "/login" ? pathname : "/dashboard";
      router.replace(`/login?next=${encodeURIComponent(intended)}`);
    }
  }, [auth.status, router, pathname]);

  useEffect(() => {
    setSidebarOpen(false);
  }, [pathname]);

  // ESC closes the mobile sidebar. Pairs with the scrim's click-outside
  // dismissal so keyboard users have an equivalent way out of the
  // overlay — required for any modal-style nav.
  useEffect(() => {
    if (!sidebarOpen) return;
    function handleKey(e: KeyboardEvent) {
      if (e.key === "Escape") setSidebarOpen(false);
    }
    window.addEventListener("keydown", handleKey);
    return () => window.removeEventListener("keydown", handleKey);
  }, [sidebarOpen]);

  // Always render the same structure so React keeps the tree stable.
  // Show a minimal topbar when loading, but never swap the entire tree.
  const isLoading = auth.status === "loading";
  const isAuth = auth.status === "authenticated";

  return (
    <AuthContext.Provider value={auth}>
      <div className="appshell">
        {isAuth ? (
          <AppTopbar
            email={auth.user.email}
            plan={auth.user.plan}
            onSignOut={auth.signOut}
            sidebarOpen={sidebarOpen}
            onToggleSidebar={() => setSidebarOpen((open) => !open)}
          />
        ) : (
          <header className="appshell-topbar">
            <div className="appshell-topbar-inner">
              <Link href="/dashboard" className="appshell-brand" aria-label="Snap Site dashboard">
                <img className="appshell-logo" src="/brand/snap-site-mark.svg" alt="" aria-hidden="true" draggable={false} />
                <img className="appshell-wordmark" src="/brand/logo-wordmark.svg" alt="" aria-hidden="true" draggable={false} />
              </Link>
            </div>
          </header>
        )}
        <div className="appshell-layout">
          {isAuth ? (
            <>
              <button
                type="button"
                className={`appshell-sidebar-scrim${sidebarOpen ? " is-open" : ""}`}
                aria-label="Close navigation menu"
                onClick={() => setSidebarOpen(false)}
              />
              <AppSidebar
                plan={auth.user.plan}
                credits={auth.entitlements.credits.balance}
                monthlyGrant={auth.entitlements.credits.monthlyGrant}
                monthly={auth.entitlements.credits.monthly}
                topup={auth.entitlements.credits.topup}
                open={sidebarOpen}
              />
            </>
          ) : isLoading ? (
            <aside className="appshell-sidebar" aria-hidden="true">
              <nav className="sidebar-nav">
                {[1, 2, 3, 4].map((i) => (
                  <div key={i} className="sidebar-nav-item sidebar-sk">
                    <span className="sk" style={{ width: 16, height: 16, borderRadius: 4 }} />
                    <span className="sk" style={{ width: 70, height: 12, borderRadius: 4 }} />
                  </div>
                ))}
              </nav>
            </aside>
          ) : null}
          <div className="appshell-body" id="main-content">
            {children}
          </div>
        </div>
      </div>
    </AuthContext.Provider>
  );
}

// --------------- Topbar ---------------

function AppTopbar({
  email,
  plan,
  onSignOut,
  sidebarOpen,
  onToggleSidebar,
}: {
  email: string;
  plan: "free" | "pro" | "agency";
  onSignOut: () => void;
  sidebarOpen: boolean;
  onToggleSidebar: () => void;
}) {
  const pathname = usePathname();
  // useTheme destructure removed — the inline theme toggle button was
  // swapped for the shared <ThemeToggle /> component, which calls
  // useTheme itself. Nothing else in this scope reads resolved/toggle.
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!menuOpen) return;
    function handleClick(e: MouseEvent) {
      if (!menuRef.current?.contains(e.target as Node)) setMenuOpen(false);
    }
    function handleKey(e: KeyboardEvent) {
      if (e.key === "Escape") setMenuOpen(false);
    }
    window.addEventListener("mousedown", handleClick);
    window.addEventListener("keydown", handleKey);
    return () => {
      window.removeEventListener("mousedown", handleClick);
      window.removeEventListener("keydown", handleKey);
    };
  }, [menuOpen]);

  const isOnNewAudit = pathname === "/audit/new";

  return (
    <header className="appshell-topbar">
      <nav className="appshell-topbar-inner">
        <div className="appshell-topbar-left">
          <button
            type="button"
            className="appshell-menu-btn"
            aria-label={sidebarOpen ? "Close navigation menu" : "Open navigation menu"}
            aria-controls="appshell-sidebar"
            aria-expanded={sidebarOpen}
            onClick={onToggleSidebar}
          >
            <span />
            <span />
            <span />
          </button>
          <Link href="/dashboard" className="appshell-brand" aria-label="Snap Site dashboard">
            <img className="appshell-logo" src="/brand/snap-site-mark.svg" alt="" aria-hidden="true" draggable={false} />
            <img className="appshell-wordmark" src="/brand/logo-wordmark.svg" alt="" aria-hidden="true" draggable={false} />
          </Link>
        </div>

        <div className="appshell-topbar-right">
          {!isOnNewAudit && (
            <Link href="/audit/new" className="appshell-new-review-btn">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" aria-hidden="true">
                <line x1="12" y1="5" x2="12" y2="19" />
                <line x1="5" y1="12" x2="19" y2="12" />
              </svg>
              <span>New audit</span>
            </Link>
          )}

          {/* Theme toggle reused from the marketing nav so the app
              topbar speaks the same visual language as the rest of
              the website. The previous inline button was an oval
              bordered pill; the shared component renders a circular
              38x38 icon-only button with no border + transparent
              background, matching the chip on the marketing site. */}
          <ThemeToggle />

          <div className="appshell-account" ref={menuRef}>
            <button
              type="button"
              className="appshell-avatar-btn"
              aria-label={`Account menu — ${email} on ${planLabel(plan)} plan`}
              aria-haspopup="menu"
              aria-expanded={menuOpen}
              onClick={() => setMenuOpen((v) => !v)}
            >
              <span className="appshell-avatar" aria-hidden="true">
                {emailInitial(email)}
              </span>
              {/* Chevron is the click affordance — tells users the chip
                  is a menu trigger, not just identity decoration.
                  Plan badge intentionally not duplicated here; the
                  dropdown header carries the email + plan together,
                  which is the single source of truth for identity. */}
              <svg
                className={`appshell-avatar-chev${menuOpen ? " is-open" : ""}`}
                width="12"
                height="12"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.5"
                strokeLinecap="round"
                strokeLinejoin="round"
                aria-hidden="true"
              >
                <polyline points="6 9 12 15 18 9" />
              </svg>
            </button>

            {menuOpen && (
              <div className="appshell-dropdown" role="menu">
                <div className="appshell-dropdown-header">
                  <p className="appshell-dropdown-email">{email}</p>
                  <span className={`appshell-dropdown-plan-badge appshell-plan-${plan}`}>
                    {planLabel(plan)}
                  </span>
                </div>
                <div className="appshell-dropdown-divider" />
                <Link href="/dashboard" className="appshell-dropdown-item" role="menuitem" onClick={() => setMenuOpen(false)}>
                  {/* House — generic "home" icon, matches Sidebar Home. */}
                  <DropdownIcon>
                    <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />
                  </DropdownIcon>
                  Home
                </Link>
                <Link href="/account" className="appshell-dropdown-item" role="menuitem" onClick={() => setMenuOpen(false)}>
                  {/* User silhouette — previously a gear/cog, which
                      duplicated the Settings icon in the sidebar. A
                      person reads as "account" without being mistaken
                      for app settings. */}
                  <DropdownIcon>
                    <circle cx="12" cy="8" r="4" />
                    <path d="M4 21c0-4.418 3.582-8 8-8s8 3.582 8 8" />
                  </DropdownIcon>
                  Account &amp; billing
                </Link>
                <div className="appshell-dropdown-divider" />
                <AppToMarketingLink
                  href="/"
                  className="appshell-dropdown-item"
                  showIcon={false}
                >
                  {/* Globe with proper meridian — the old icon was a
                      circle with a single horizontal line through it,
                      which looked like a "no entry" / muted glyph and
                      didn't read as a globe at all. */}
                  <DropdownIcon>
                    <circle cx="12" cy="12" r="10" />
                    <line x1="2" y1="12" x2="22" y2="12" />
                    <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z" />
                  </DropdownIcon>
                  Go to site
                </AppToMarketingLink>
                <button
                  type="button"
                  className="appshell-dropdown-item appshell-dropdown-signout"
                  role="menuitem"
                  onClick={() => { setMenuOpen(false); onSignOut(); }}
                >
                  {/* Door + outbound arrow — kept as-is, it reads
                      cleanly as sign-out. */}
                  <DropdownIcon>
                    <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
                    <path d="M16 17l5-5-5-5M21 12H9" />
                  </DropdownIcon>
                  Sign out
                </button>
              </div>
            )}
          </div>
        </div>
      </nav>
    </header>
  );
}

// --------------- Sidebar ---------------

function AppSidebar({
  plan,
  credits,
  monthlyGrant,
  monthly,
  topup,
  open,
}: {
  plan: "free" | "pro" | "agency";
  credits: number;
  monthlyGrant: number;
  monthly?: number;
  topup?: number;
  open: boolean;
}) {
  const pathname = usePathname();

  // No progress bar here — balance can legitimately exceed the monthly
  // grant once a user tops up, which made the old "X / Y" framing read
  // as a broken ratio. Big balance + caption + links communicate the
  // same information without the visual lie.
  return (
    <aside id="appshell-sidebar" className={`appshell-sidebar${open ? " is-open" : ""}`} aria-label="App navigation">
      <nav className="sidebar-nav">
        <SidebarLink
          href="/dashboard"
          label="Home"
          navKey="home"
          icon="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"
          active={pathname === "/dashboard"}
        />
        <SidebarLink
          href="/audit/new"
          label="New audit"
          navKey="new-audit"
          icon="M12 2a10 10 0 1 0 0 20 10 10 0 0 0 0-20z"
          iconExtra="M12 8v8M8 12h8"
          active={pathname === "/audit/new"}
        />
        <SidebarLink
          href="/usage"
          label="Usage"
          navKey="usage"
          icon="M3 3v18h18"
          iconExtra="M7 15l3-3 3 2 5-6"
          active={pathname === "/usage"}
        />
        <SidebarFiles pathname={pathname} />
        <SidebarLink
          href="/trash"
          label="Trash"
          navKey="trash"
          icon="M3 6h18M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"
          active={pathname === "/trash"}
        />
        <SidebarLink
          href="/settings"
          label="Settings"
          navKey="settings"
          icon="M12 15a3 3 0 1 0 0-6 3 3 0 0 0 0 6z"
          iconExtra="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83-2.83l.06-.06A1.65 1.65 0 0 0 4.68 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 2.83-2.83l.06.06A1.65 1.65 0 0 0 9 4.68a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 2.83l-.06.06A1.65 1.65 0 0 0 19.4 9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z"
          active={pathname === "/settings"}
        />
      </nav>

      <div className="sidebar-footer">
        <CreditsBlock
          plan={plan}
          credits={credits}
          monthlyGrant={monthlyGrant}
          monthly={monthly}
          topup={topup}
        />
      </div>
    </aside>
  );
}

/**
 * Sidebar credits widget.
 *
 * Default state shows just the total — that's the one number a user
 * actually needs at-a-glance ("can I run another audit?"). The
 * Monthly + Top-up breakdown is progressive disclosure: tap the row
 * to expand and see where the balance comes from.
 *
 * `monthly` and `topup` come straight from the backend's per-bucket
 * balances (free + subscription vs purchased). Older API responses may
 * omit them — in that case we fall back to inferring the split from the
 * flat balance vs the monthly grant, which is only approximate (it can't
 * tell a topped-up monthly balance from leftover monthly credits).
 *
 * The toggle disappears when there's nothing meaningful to disclose —
 * no monthly grant, or the whole balance is monthly and at the grant.
 */
function CreditsBlock({
  plan,
  credits,
  monthlyGrant,
  monthly,
  topup,
}: {
  plan: "free" | "pro" | "agency";
  credits: number;
  monthlyGrant: number;
  monthly?: number;
  topup?: number;
}) {
  const [open, setOpen] = useState(false);
  const hasGrant = monthlyGrant > 0;
  // Prefer the authoritative per-bucket numbers; fall back to inference.
  const monthlyRaw =
    typeof monthly === "number"
      ? monthly
      : hasGrant
        ? Math.min(credits, monthlyGrant)
        : 0;
  const topUpRaw =
    typeof topup === "number"
      ? topup
      : hasGrant
        ? Math.max(0, credits - monthlyGrant)
        : 0;
  // Reconcile the split against the authoritative balance (the header).
  // The backend clamps an overdrawn bucket to 0 for display, so when the
  // monthly bucket goes negative the raw parts no longer add up to the
  // balance — e.g. "Monthly 0 + Top-up 380" under a header reading "39".
  // Only when the parts don't sum to the balance do we re-derive them:
  // fill monthly first (the use-it-or-lose-it bucket, capped at the
  // balance), then attribute whatever remains to top-up. In the normal
  // case (both buckets non-negative) this leaves the numbers untouched.
  const balanceForSplit = Math.max(0, credits);
  const partsReconcile =
    Math.max(0, monthlyRaw) + Math.max(0, topUpRaw) === balanceForSplit;
  const monthlyPart = partsReconcile
    ? Math.max(0, monthlyRaw)
    : Math.min(Math.max(0, monthlyRaw), balanceForSplit);
  const topUp = partsReconcile
    ? Math.max(0, topUpRaw)
    : Math.max(0, balanceForSplit - monthlyPart);
  const isBelowGrant = hasGrant && monthlyPart < monthlyGrant;
  // Breakdown adds info only when there's a monthly grant and either the
  // monthly bucket is below grant or there are top-up credits to show.
  const hasBreakdown = hasGrant && (isBelowGrant || topUp > 0);

  const Head = (
    <>
      <span className="sidebar-credits-label">Credits</span>
      <span className="sidebar-credits-total">{credits.toLocaleString()}</span>
      {hasBreakdown ? (
        <svg
          className={`sidebar-credits-chev${open ? " is-open" : ""}`}
          width="11"
          height="11"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2.5"
          strokeLinecap="round"
          strokeLinejoin="round"
          aria-hidden="true"
        >
          <polyline points="6 9 12 15 18 9" />
        </svg>
      ) : null}
    </>
  );

  return (
    <div className="sidebar-credits">
      {hasBreakdown ? (
        <button
          type="button"
          className="sidebar-credits-head sidebar-credits-toggle"
          onClick={() => setOpen((v) => !v)}
          aria-expanded={open}
          aria-controls="sidebar-credits-breakdown"
        >
          {Head}
        </button>
      ) : (
        <div className="sidebar-credits-head">{Head}</div>
      )}

      {hasBreakdown && open ? (
        <>
          <dl id="sidebar-credits-breakdown" className="sidebar-credits-breakdown">
            <div className="sidebar-credits-row">
              <dt>Monthly</dt>
              <dd>
                {monthlyPart.toLocaleString()}
                {isBelowGrant ? (
                  <span className="sidebar-credits-of"> / {monthlyGrant.toLocaleString()}</span>
                ) : null}
              </dd>
            </div>
            {topUp > 0 ? (
              <div className="sidebar-credits-row">
                <dt>Top-up</dt>
                <dd>{topUp.toLocaleString()}</dd>
              </div>
            ) : null}
          </dl>
          {/* One-line legend — answers "what do these two rows mean?".
              Monthly is the recurring grant (resets each month, doesn't
              roll over); top-up is purchased and carries over until it
              expires. */}
          <p className="sidebar-credits-note">
            Monthly resets each month. Top-up rolls over.
          </p>
        </>
      ) : null}

      {/* In-app routing to Account → Plan & billing — no bounce out to
          the marketing site. Free users land directly on the upgrade
          ladder (#upgrade); paid users land on their plan card (#plan). */}
      <Link
        href={plan === "free" ? "/account#upgrade" : "/account#plan"}
        className="sidebar-credits-link"
      >
        {plan === "free" ? "Upgrade plan →" : "Manage plan →"}
      </Link>
    </div>
  );
}

const SIDEBAR_FILES_EXPANDED_KEY = "snap-site:sidebar-files-expanded";

function SidebarFiles({ pathname }: { pathname: string | null }) {
  const { folders } = useFolders();
  const onProjects =
    pathname === "/projects" || (pathname?.startsWith("/projects/") ?? false);
  // Top folders are an at-a-glance jump-list — sort by recently updated
  // so the user's working set bubbles to the top.
  const recent = folders
    .filter((f) => !f.trashedAt && f.parentId === null)
    .sort((a, b) => b.updatedAt - a.updatedAt)
    .slice(0, 5);
  const hasFolders = recent.length > 0;

  // Accordion state: null = not yet hydrated from storage, true/false =
  // user preference. We start null and resolve on mount so SSR doesn't
  // flash a different state than the user previously chose. The default
  // (when no preference is stored) is to be open when the user is
  // already on a /projects route — they're clearly working in Files,
  // so the tree should be visible. Otherwise stay collapsed to keep
  // the sidebar tidy.
  const [expanded, setExpanded] = useState<boolean | null>(null);

  useEffect(() => {
    if (typeof window === "undefined") return;
    const stored = window.localStorage.getItem(SIDEBAR_FILES_EXPANDED_KEY);
    if (stored === "true") setExpanded(true);
    else if (stored === "false") setExpanded(false);
    else setExpanded(onProjects);
    // Intentionally only runs once on mount — afterwards we respect
    // the user's explicit toggle and don't override on route change.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const isExpanded = expanded ?? onProjects;

  function toggle() {
    const next = !isExpanded;
    setExpanded(next);
    if (typeof window !== "undefined") {
      window.localStorage.setItem(SIDEBAR_FILES_EXPANDED_KEY, String(next));
    }
  }

  return (
    <div className="sidebar-files-group">
      {/* Row uses a wrapper so the Link and the chevron toggle are
          siblings rather than nested interactives. Nesting a <button>
          inside an <a> is invalid HTML and breaks keyboard navigation
          (Enter on the link would also trigger the inner button). */}
      <div className="sidebar-files-row">
        <SidebarLink
          href="/projects"
          label="Files"
          navKey="files"
          icon="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z"
          active={onProjects && (!pathname || pathname === "/projects")}
        />
        {hasFolders ? (
          <button
            type="button"
            className="sidebar-files-toggle"
            onClick={toggle}
            aria-expanded={isExpanded}
            aria-controls="sidebar-files-tree"
            aria-label={isExpanded ? "Collapse folders" : "Expand folders"}
          >
            <svg
              width="14"
              height="14"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.5"
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden="true"
              className={`sidebar-files-chevron${isExpanded ? " is-open" : ""}`}
            >
              <polyline points="9 6 15 12 9 18" />
            </svg>
          </button>
        ) : null}
      </div>
      {hasFolders && isExpanded ? (
        <ul className="sidebar-files-tree" id="sidebar-files-tree">
          {recent.map((f) => {
            const href = `/projects/${encodeURIComponent(f.id)}`;
            return (
              <li key={f.id}>
                <Link
                  href={href}
                  className={`sidebar-files-leaf${pathname === href ? " active" : ""}`}
                  title={f.name}
                >
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                    <path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z" />
                  </svg>
                  <span>{f.name}</span>
                </Link>
              </li>
            );
          })}
        </ul>
      ) : null}
    </div>
  );
}

function SidebarLink({
  href,
  label,
  icon,
  iconExtra,
  active = false,
  soon = false,
  navKey,
}: {
  href: string;
  label: string;
  icon: string;
  iconExtra?: string;
  active?: boolean;
  soon?: boolean;
  /**
   * Identifier for per-icon hover animations. Each nav row gets a
   * unique hover micro-interaction (settings cog rotates, trash lid
   * shakes, plus rotates 90°). Without this key, the CSS would have
   * to scrape the label text — fragile if labels change.
   */
  navKey?: string;
}) {
  const className = [
    "sidebar-nav-item",
    active ? "active" : "",
    soon ? "sidebar-soon" : "",
  ].filter(Boolean).join(" ");

  const inner = (
    <>
      <svg className="sidebar-nav-icon" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <path d={icon} />
        {iconExtra && <path d={iconExtra} />}
      </svg>
      <span className="sidebar-nav-label">{label}</span>
      {soon && <span className="sidebar-soon-badge">Soon</span>}
    </>
  );

  // "Soon" items are teasers — they advertise unshipped features without
  // navigating anywhere. Render a non-link span so clicking does nothing
  // (instead of routing to whatever placeholder href was passed in,
  // which previously made Favorites lie by sending users back to the
  // dashboard).
  if (soon) {
    // Plain non-interactive span. No href, no role, no aria-disabled —
    // a span isn't in the a11y tree by default and the visible "Soon"
    // badge already communicates "not yet available".
    return <span className={className} data-nav={navKey}>{inner}</span>;
  }

  return (
    <Link
      href={href}
      className={className}
      aria-current={active ? "page" : undefined}
      data-nav={navKey}
    >
      {inner}
    </Link>
  );
}

/* --- Small inline icons --- */

/**
 * Wrapper for the small icons that lead each row in the account
 * dropdown. Children are arbitrary SVG shapes (paths, circles, lines)
 * so icons that need 3+ strokes (like the globe, with meridians) can
 * compose them without a special-case prop. All icons share the same
 * size, viewBox, stroke, and `.appshell-dropdown-icon` class — the
 * wrapper is the single source of truth for those.
 */
function DropdownIcon({ children }: { children: ReactNode }) {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" className="appshell-dropdown-icon">
      {children}
    </svg>
  );
}

// MoonIcon/SunIcon removed — they only fed the now-removed inline
// theme toggle. The shared <ThemeToggle /> from the marketing nav
// owns its own glyphs.
